<template>
  <teleport to="#teleported">
    <div
      v-if="open"
      class="fixed inset-0 z-20"
      @click="close"
      @contextmenu.prevent
    ></div>

    <Transition name="scale">
      <ul
        v-if="open"
        v-bind="$attrs"
        id="paragraph-context-menu"
        ref="element"
        class="fixed z-30 mt-2 flex origin-top-left list-none flex-col rounded-md bg-white px-1 py-2 font-sans shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
        :class="{ 'divide-y divide-gray-200': items.length }"
        :style="contextMenuPosition"
        role="menu"
        :aria-label="$t('ui.actions')"
        tabindex="-1"
        @mouseup.stop
        @touchend.stop
        @click.stop
        @pointerup.stop
      >
        <li
          v-for="entry in items"
          :key="`${entry.id}_${entry.type}`"
          :class="{ list: items.length }"
          role="menuitem"
          tabindex="-1"
        >
          <MoveContext
            v-if="movable"
            @click="handleMove(entry as INote | ISchema | ILinkChain)"
          />
          <EditContext
            v-if="entry.type === 'link_chain'"
            @click="handleEdit(entry)"
          />
          <ContextEntry
            class="w-full"
            :entry="entry"
            :legalText="legalText"
            :legalTextParagraph="legalTextParagraph"
            @close="close"
            @delete="handleDelete"
          />
        </li>
      </ul>
    </Transition>
  </teleport>
</template>

<script lang="ts" setup>
import type {
  ILinkChain,
  INote,
  IParagraphEntry,
  ISchema,
} from "~/models/IUserEntry";
import type { ILegalText } from "~/models/ILegalText";
import type { ILegalTextParagraph } from "~/models/ILegalTextParagraph";

const props = defineProps<{
  open?: boolean;
  movable?: boolean;
  items: IParagraphEntry[];
  legalText: ILegalText;
  legalTextParagraph: ILegalTextParagraph;
}>();
const emits = defineEmits<{
  close: [];
  delete: [entry: IParagraphEntry];
  move: [entry: ISchema | INote | ILinkChain];
  editLink: [entry: ILinkChain];
}>();
const element = ref();
const scrollParent = ref();

// https://gist.github.com/twxia/bb20843c495a49644be6ea3804c0d775
function getScrollParent(node: Node | null): Node | null {
  const isElement = node instanceof HTMLElement;

  if (!isElement) {
    return null;
  }

  const overflowY = getComputedStyle(node).overflowY;
  const isScrollable = overflowY !== "visible" && overflowY !== "hidden";

  if (isScrollable && node.scrollHeight >= node.clientHeight) {
    return node;
  }

  return getScrollParent(node.parentNode) || document.body;
}

onMounted(() => (scrollParent.value = getScrollParent(unrefElement(element))));

useEventListener(scrollParent, "scroll", () => props.open && emits("close"));

const { x, y } = useMouse();

const position = reactive({ x: 0, y: 0 });

whenever(
  () => props.open,
  () => {
    position.x = x.value;
    position.y = y.value;
  }
);

const contextMenuPosition = computed(() => {
  const topValue = Math.min(
    position.y,
    window.innerHeight - element?.value?.clientHeight - 20
  );

  const leftValue = Math.min(
    position.x,
    window.innerWidth - element?.value?.clientWidth - 20
  );

  return `top:${topValue}px; left:${leftValue + 15}px;`;
});
const close = () => {
  emits("close");
};

const handleDelete = (entry: IParagraphEntry) => {
  emits("delete", entry);
  close();
};

const handleMove = (entry: ISchema | INote | ILinkChain) => {
  emits("move", entry);
  close();
};
const handleEdit = (entry: ILinkChain) => {
  emits("editLink", entry);
  close();
};
</script>
<style lang="scss" scoped>
.list {
  @apply py-[.4rem];

  &:first-child {
    @apply pt-0;
  }

  &:last-child {
    @apply pb-0;
  }
}
</style>
